/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import FieldArray from '../FieldArray';
import TextField from '../TextField';
import Checkbox from '../Checkbox';
import DynamicSearchFilter from '../DynamicSearchFilter';
import MatchReport from './matchReport';
import * as relevantApi from '../../api/relevant';

const { ADVERTISER_MAPPED_AWAY } = MatchReport;
const WORD_MIN_LENGTH = 3;
const relevant = relevantApi.DynamicExports;

class SingleMapping extends React.Component {
	dynProps(mappingDim) {
		const { type, metric } = this.props;
		return {
			serverCall: (objNrs) => relevant[mappingDim.clsName].call('getObjects', { objNrs }),
			type,
			metric,
			fullWidth: true,
			noMapping: true,
			createUsingDimObject: mappingDim,
			dimension: 'advNr',
			label: mappingDim.dimension,
			groupBy: mappingDim.dimension,
			showMoreLabel: mappingDim.pluralName,
			noObjectCreate: mappingDim.sysSpecific,
		};
	}

	render() {
		const {
			mapping, type, field, metric, knownEntities, mappingDim, checkForbidden, onTargetChange, targetRefs,
		} = this.props;
		const selected = _.without(mapping.advertisers, mapping.targetAdvNr).map(String);
		const sharedProps = {
			...this.dynProps(mappingDim),
			key: mapping.targetAdvNr.toString(),
			checkForbidden,
		};
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<DynamicSearchFilter
						{...sharedProps}
						ref={targetRefs[mappingDim.name]}
						selected={[mapping.targetAdvNr.toString()]}
						onlySingleSelect
						textFieldLabel={`Change Target ${mappingDim.uiCName}`}
						onSingleSelect={(nr, obj) => onTargetChange(nr, obj, mappingDim)}
						noDelete
						displaySingleSelected
					/>
					<Card style={{ overflow: 'visible' }}>
						<CardContent>
							<Typography variant="h2">
								Directly mapped
								{' '}
								{mappingDim.pluralName}
							</Typography>
							<DynamicSearchFilter
								{...sharedProps}
								selected={selected}
								onChange={(items, advs) => {
									mapping.advertisers = items.map((nr) => parseInt(nr, 10));
									Object.assign(knownEntities[mappingDim.name], _.keyBy(advs, 'advNr'));
									this.forceUpdate();
								}}
								textFieldLabel={`Add ${mappingDim.uiCName}(s)`}
								noMapping
							/>
						</CardContent>
					</Card>
				</Grid>
				{!!mappingDim.crossDims.length && (
					<Grid item xs={12}>
						<Card style={{ overflow: 'visible' }}>
							<CardContent>
								<Typography variant="h2">
									Automatic assignments
								</Typography>
								<Grid container spacing={3}>
									{mappingDim.crossDims.filter((dim) => !dim.isStatic).map((dim) => (
										<Grid item xs={12} key={dim.name}>
											<DynamicSearchFilter
												{...this.dynProps(dim)}
												ref={targetRefs[dim.name]}
												selected={_.filter([mapping.crossDimMapping?.[dim.name]?.toString() || ''])}
												textFieldLabel={`Assign ${dim.uiCName}`}
												onSingleSelect={(nr, obj) => onTargetChange(nr, obj, dim)}
												displaySingleSelected
												onlySingleSelect
												delayReportLoad
											/>
										</Grid>
									))}
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				)}
				<Grid item xs={12}>
					<Card>
						<CardContent>
							<Typography variant="h2">
								Map words / phrases
							</Typography>
							<FieldArray
								model={mapping}
								field={field}
								name="byWords"
								update={() => this.forceUpdate()}
								render={(fld, elm) => (
									<Grid container spacing={3} alignItems="center">
										<Grid item xs={6}>
											<TextField
												required
												label="Word / Phrase"
												minlen={WORD_MIN_LENGTH}
												{...fld('word')}
												fullWidth
											/>
										</Grid>
										<Grid item xs={4}>
											<Checkbox label="Exact match" {...fld('exactMatch')} />
										</Grid>
										<Grid item xs={2}>
											{(elm.word || '').length >= WORD_MIN_LENGTH && (
												<MatchReport
													metric={metric}
													type={type}
													dimType={mappingDim.name}
													mapping={{
														mappings: [{
															targetAdvNr: ADVERTISER_MAPPED_AWAY,
															advertisers: [],
															byWords: [elm],
														}],
														dimType: mappingDim.name,
													}}
												/>
											)}
										</Grid>
									</Grid>
								)}
							/>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		);
	}
}

SingleMapping.propTypes = {
	targetRefs: PropTypes.object.isRequired,
	field: PropTypes.func.isRequired,
	type: PropTypes.string.isRequired,
	mapping: PropTypes.object.isRequired,
	metric: PropTypes.string.isRequired,
	knownEntities: PropTypes.object.isRequired,
	mappingDim: PropTypes.object.isRequired,
	checkForbidden: PropTypes.func.isRequired,
	onTargetChange: PropTypes.func.isRequired,
};

SingleMapping.defaultProps = {
};

export default SingleMapping;
